<template>
  <div class="w-full px-6 py-6">
    <router-link to="/admin/users"
      class="inline-flex items-center gap-1.5 mb-3 px-3 py-1.5 rounded-md text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
      Back to users
    </router-link>
    <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">
      <router-link to="/admin/users" class="hover:underline">Users</router-link>
      <span> / </span>
      <span>{{ displayName }}</span>
    </div>

    <div class="flex items-center gap-3 mb-4 flex-wrap">
      <div
        class="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center font-medium dark:text-gray-100 flex-shrink-0">
        {{ initials }}
      </div>
      <div class="min-w-0 flex-1">
        <h1 class="text-xl sm:text-2xl font-semibold dark:text-gray-100 truncate">{{ displayName }}</h1>
        <div class="text-sm text-gray-500 truncate">
          {{ summary.email }}<span v-if="summary.title"> · {{ summary.title }}</span>
        </div>
      </div>
      <span class="inline-flex items-center gap-1.5 text-xs dark:text-gray-200">
        <span :class="['w-2 h-2 rounded-full', summary.active ? 'bg-green-500' : 'bg-gray-400']"></span>
        {{ summary.active ? 'Active' : 'Inactive' }}
      </span>
      <span
        :class="isAzureSourced ? 'bg-sky-100 text-sky-700 dark:bg-sky-800 dark:text-sky-100' : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200'"
        class="inline-block text-xs px-2 py-0.5 rounded-full"
        :title="isAzureSourced ? 'Synced from Azure / Entra ID' : 'Manually created in ATMS'">
        {{ isAzureSourced ? 'Azure' : 'Manual' }}
      </span>
      <button @click="openHistory"
        class="ml-auto px-3 py-2 rounded-md text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100">
        View edit history
      </button>
    </div>

    <div class="border-b border-gray-200 dark:border-gray-700 mb-6 -mx-6 px-6 overflow-x-auto">
      <nav class="flex gap-6 text-sm whitespace-nowrap">
        <button v-for="t in tabs" :key="t.key" @click="activeTab = t.key"
          :class="['py-3 border-b-2 cursor-pointer', activeTab === t.key ? 'border-yellow-500 text-gray-900 dark:text-white font-medium' : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white']">
          {{ t.label }}
          <span v-if="t.badge !== undefined"
            class="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600">{{ t.badge }}</span>
        </button>
      </nav>
    </div>

    <!-- =========== PROFILE TAB =========== -->
    <div v-if="activeTab === 'profile'" class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-8">
        <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-6 shadow-sm">
          <h2 class="font-medium mb-4 dark:text-gray-100">Profile details</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">First name</label>
              <input v-model="form.name_first"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Last name</label>
              <input v-model="form.name_last"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div class="md:col-span-2">
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Email</label>
              <input v-model="form.email" type="email"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
              <div class="text-xs text-amber-600 mt-1" v-if="summary.address_book_count">
                Changing this email will re-link the user's {{ summary.address_book_count }} address book entries.
              </div>
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Phone primary</label>
              <input v-model="form.phone_primary"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Phone primary ext.</label>
              <input v-model="form.phone_primary_ext"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Phone secondary</label>
              <input v-model="form.phone_secondary"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Phone secondary ext.</label>
              <input v-model="form.phone_secondary_ext"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div v-if="isInternalUser">
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Title</label>
              <input v-model="form.title"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div v-else>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Job title <span
                  class="text-gray-400">(from Azure)</span></label>
              <input :value="form.title || ''" disabled
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-300 px-3 py-2 text-sm" />
            </div>
            <div v-if="isInternalUser">
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Department</label>
              <select v-model.number="form.aim_dept"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm">
                <option :value="0">— None —</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>
            <div v-else>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Department <span
                  class="text-gray-400">(from Azure)</span></label>
              <input :value="form.department || ''" disabled
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-300 px-3 py-2 text-sm" />
            </div>
            <div v-if="isInternalUser">
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Manager</label>
              <select v-model.number="form.manager_id"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm">
                <option :value="0">— Unassigned —</option>
                <option v-for="m in managerOptions" :key="m.id" :value="m.id">{{ m.name }}</option>
              </select>
            </div>
            <div v-if="isExternalUser">
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Initials</label>
              <input v-model="form.initials" maxlength="10"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
            </div>
            <div v-if="isExternalUser">
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Office location</label>
              <select v-model.number="form.officelocation"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm">
                <option :value="0">— None —</option>
                <option v-for="o in officeLocations" :key="o.id" :value="o.id">{{ o.label }}</option>
              </select>
            </div>
            <div class="md:col-span-2">
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Notes</label>
              <textarea v-model="form.notes" rows="3"
                class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm"></textarea>
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">User type</label>
              <div class="flex items-center gap-2">
                <select v-model="form.user_type"
                  class="flex-1 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm">
                  <option value="non-billable">non-billable</option>
                  <option value="cloud">cloud</option>
                  <option value="standard">standard</option>
                </select>
                <label class="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                  <input type="checkbox" v-model="userTypePermanent" /> Permanent
                </label>
                <button @click="saveUserType" :disabled="savingUserType"
                  class="px-2 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100 disabled:opacity-50">
                  Override
                </button>
              </div>
              <p v-if="form.user_type_overridden" class="text-[10px] text-amber-700 dark:text-amber-300 mt-1">
                Manually overridden{{ form.user_type_override_permanent ? ' (permanent)' : '' }}.
              </p>
            </div>
          </div>

          <div v-if="isInternalUser" class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <h3 class="font-medium mb-3 dark:text-gray-100">Emergency contact</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Name</label>
                <input v-model="form.emergency_name"
                  class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
              </div>
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Relationship</label>
                <input v-model="form.emergency_relation"
                  class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
              </div>
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Phone</label>
                <input v-model="form.emergency_phone"
                  class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
              </div>
            </div>
          </div>

          <div v-if="isInternalUser" class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <label class="flex items-center gap-2 text-sm dark:text-gray-100">
              <input type="checkbox" v-model="partTime"> Part-time employee
            </label>
          </div>

          <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
            <div>
              <div class="font-medium text-sm dark:text-gray-100">Active</div>
              <div class="text-xs text-gray-500">Inactive users can't log in.</div>
            </div>
            <label class="inline-flex items-center"
              :class="canManageStatus ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'"
              :title="canManageStatus ? '' : 'You can only change status for users at or below your privilege level.'">
              <input type="checkbox" v-model="profileActive" :disabled="!canManageStatus" class="sr-only peer">
              <div
                class="w-11 h-6 bg-gray-200 dark:bg-gray-500 rounded-full peer peer-checked:bg-yellow-500 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5">
              </div>
            </label>
          </div>

          <div
            class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600 flex items-center justify-end gap-2 flex-wrap">
            <button @click="load"
              class="px-4 py-2 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600">Discard
              changes</button>
            <button @click="save" :disabled="saving"
              class="px-4 py-2 rounded-md text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-medium disabled:opacity-50">
              {{ saving ? 'Saving…' : 'Save profile' }}
            </button>
          </div>
        </div>
      </div>

      <aside class="col-span-12 lg:col-span-4 space-y-4">
        <div
          class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 shadow-sm dark:text-gray-100">
          <div class="font-medium mb-2">At a glance</div>
          <dl class="text-sm space-y-1">
            <div class="flex justify-between">
              <dt class="text-gray-500">User ID</dt>
              <dd>{{ summary.id }}</dd>
            </div>
            <div class="flex justify-between gap-2">
              <dt class="text-gray-500 flex-shrink-0">Azure ID</dt>
              <dd class="text-xs break-all text-right font-mono">{{ summary.azure_id || '—' }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">Created</dt>
              <dd>{{ formatDate(summary.created_at) }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">Last login</dt>
              <dd>{{ formatDateTime(summary.last_portal_login) }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">Roles</dt>
              <dd>{{ summary.roles_count }} active</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">Companies</dt>
              <dd>{{ summary.companies_count }} assigned</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">Address book</dt>
              <dd>{{ summary.address_book_count }} entries</dd>
            </div>
          </dl>
        </div>
        <div v-if="canSetPassword"
          class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 shadow-sm dark:text-gray-100">
          <div class="font-medium mb-2">Set password</div>
          <p class="text-xs text-gray-500 mb-2">Directly set a new portal password (min 8 characters). Takes effect
            immediately.</p>
          <input type="password" v-model="newPassword" placeholder="New password" autocomplete="new-password"
            class="w-full mb-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
          <input type="password" v-model="confirmPassword" placeholder="Confirm password" autocomplete="new-password"
            class="w-full mb-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm" />
          <p v-if="pwError" class="text-red-500 text-xs mb-2">{{ pwError }}</p>
          <button @click="setUserPassword" :disabled="settingPassword"
            class="w-full px-3 py-2 rounded-md text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-medium disabled:opacity-50">
            {{ settingPassword ? 'Saving…' : 'Set password' }}
          </button>
        </div>
        <div class="bg-white dark:bg-gray-700 border border-red-200 dark:border-red-900 rounded-xl p-4 shadow-sm">
          <div class="font-medium text-red-600 dark:text-red-400 mb-2">Danger zone</div>
          <div class="space-y-2">
            <button @click="sendPasswordReset" :disabled="dangerBusy"
              class="w-full text-left px-3 py-2 rounded-md bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-sm text-red-700 dark:text-red-200 disabled:opacity-50">
              Send password reset email
            </button>
            <button @click="revokeTokens" :disabled="dangerBusy"
              class="w-full text-left px-3 py-2 rounded-md bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-sm text-red-700 dark:text-red-200 disabled:opacity-50">
              Revoke all access tokens
            </button>
            <button @click="disableAccount" :disabled="dangerBusy"
              class="w-full text-left px-3 py-2 rounded-md bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-sm text-red-700 dark:text-red-200 disabled:opacity-50">
              Disable account
            </button>
          </div>
        </div>
      </aside>
    </div>

    <!-- =========== ROLES TAB =========== -->
    <div v-if="activeTab === 'roles'" class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-9">
        <div
          class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-sm overflow-hidden">
          <div
            class="px-4 py-3 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between flex-wrap gap-2">
            <div class="font-medium dark:text-gray-100">Roles</div>
            <div class="text-xs text-gray-500">Toggle a role on/off. The active flag is updated; history is preserved.
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm min-w-[600px]">
              <thead class="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-700 dark:text-gray-300">
                <tr>
                  <th class="text-left px-4 py-3">ID</th>
                  <th class="text-left px-4 py-3">Role</th>
                  <th class="text-left px-4 py-3">Type</th>
                  <th class="text-right px-4 py-3">Active</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
                <tr v-for="r in roles" :key="r.id" class="hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-100">
                  <td class="px-4 py-3 text-gray-500">{{ r.id }}</td>
                  <td class="px-4 py-3 font-medium">{{ r.name }}</td>
                  <td class="px-4 py-3">
                    <span
                      :class="r.type === 'Internal' ? 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-100' : (r.type === 'External' ? 'bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-100' : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-100')"
                      class="text-xs px-2 py-0.5 rounded-full">{{ r.type }}</span>
                    <span v-if="r.requires_company" class="text-xs text-red-500 ml-1">requires company</span>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <label class="inline-flex items-center"
                      :class="r.id < callerMinRole ? 'cursor-not-allowed' : 'cursor-pointer'"
                      :title="r.id < callerMinRole ? 'You can only manage roles at or below your own privilege level.' : ''">
                      <input type="checkbox" :checked="r.active" :disabled="roleBusy || r.id < callerMinRole"
                        @change="toggleRole(r, $event.target.checked)" class="sr-only peer">
                      <div
                        class="w-11 h-6 bg-gray-200 dark:bg-gray-500 rounded-full peer peer-checked:bg-yellow-500 peer-disabled:opacity-50 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5">
                      </div>
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <aside class="col-span-12 lg:col-span-3">
        <div
          class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-sm dark:text-amber-100">
          <div class="font-medium text-amber-800 dark:text-amber-200 mb-1">Heads up</div>
          <div class="text-amber-800 dark:text-amber-200">External roles (60 / 70 / 80) require at least one company
            assignment.</div>
        </div>
      </aside>
    </div>

    <!-- =========== COMPANIES TAB =========== -->
    <div v-if="activeTab === 'companies'" class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-8">
        <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 shadow-sm">
          <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Add a company</label>
          <input v-model="companySearch" @input="debouncedCompanySearch"
            class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 px-3 py-2 text-sm"
            placeholder="Search clients by name…" />
          <div v-if="companyResults.length"
            class="mt-3 max-h-64 overflow-auto border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-100 dark:divide-gray-600">
            <div v-for="c in companyResults" :key="c.id"
              class="px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600 text-sm dark:text-gray-100">
              <div class="font-medium">{{ c.companyname }}</div>
              <button @click="addCompany(c)" class="text-xs px-2 py-1 rounded-md bg-yellow-500 text-white">+
                Add</button>
            </div>
          </div>

          <div class="mt-4">
            <div class="text-xs uppercase tracking-wide text-gray-500 mb-2">Currently assigned ({{ companies.length }})
            </div>
            <div class="flex flex-wrap gap-2">
              <span v-for="c in companies" :key="c.id"
                class="bg-yellow-500 text-white rounded-full px-3 py-1 text-sm flex items-center gap-1">
                {{ c.companyname }}
                <button @click="removeCompany(c)" class="ml-1 hover:text-yellow-200">&times;</button>
              </span>
              <span v-if="!companies.length" class="text-sm text-gray-500">No companies assigned.</span>
            </div>
          </div>
        </div>
      </div>
      <aside class="col-span-12 lg:col-span-4">
        <div
          class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 shadow-sm dark:text-gray-100">
          <div class="font-medium mb-2 text-sm">Why this matters</div>
          <p class="text-sm text-gray-600 dark:text-gray-300">External roles (Customer, Customer Admin, External Tech)
            must be scoped to one or more companies. The user's address book entries are also filtered by their assigned
            companies.</p>
        </div>
      </aside>
    </div>

    <!-- =========== PERMISSIONS TAB =========== -->
    <div v-if="activeTab === 'permissions'" class="space-y-6">

    <!-- Company ticket notifications — any external user, off by default -->
    <div v-if="isExternalUser"
      class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-sm overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
        <div class="font-medium dark:text-gray-100">Company ticket notifications</div>
        <div class="text-xs text-gray-500 mt-1">
          Email this user about every ticket for their company — not just the ones they open. Off by default; changes
          save immediately.
        </div>
      </div>
      <ul class="divide-y divide-gray-100 dark:divide-gray-600">
        <li class="px-4 py-4 flex items-start gap-4">
          <div class="flex-1">
            <div class="font-medium dark:text-gray-100">Notify on all created tickets</div>
            <div class="text-xs text-gray-500 mt-1">Send an email whenever any ticket for this company is created.</div>
          </div>
          <label class="inline-flex items-center pt-1 cursor-pointer">
            <input type="checkbox" v-model="notifyCreated" :disabled="notifyBusy" @change="saveNotifyFlags"
              class="sr-only peer">
            <div
              class="w-11 h-6 bg-gray-200 dark:bg-gray-500 rounded-full peer peer-checked:bg-yellow-500 peer-disabled:opacity-50 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5">
            </div>
          </label>
        </li>
        <li class="px-4 py-4 flex items-start gap-4">
          <div class="flex-1">
            <div class="font-medium dark:text-gray-100">Notify on all closed tickets</div>
            <div class="text-xs text-gray-500 mt-1">Send an email whenever any ticket for this company is closed.</div>
          </div>
          <label class="inline-flex items-center pt-1 cursor-pointer">
            <input type="checkbox" v-model="notifyClosed" :disabled="notifyBusy" @change="saveNotifyFlags"
              class="sr-only peer">
            <div
              class="w-11 h-6 bg-gray-200 dark:bg-gray-500 rounded-full peer peer-checked:bg-yellow-500 peer-disabled:opacity-50 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5">
            </div>
          </label>
        </li>
      </ul>
    </div>

    <!-- Frontend page overrides — hidden for contacts (they never log into the portal) -->
    <div v-if="!isContactUser"
      class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-sm overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
        <div class="font-medium dark:text-gray-100">Frontend page overrides</div>
        <div class="text-xs text-gray-500 mt-1">
          These toggles control the Vue navigation only — they let a user see specific pages even when their role
          wouldn't normally grant access.
          Backend access control (what API actions a user can perform) is governed by their role and is not changed
          here.
        </div>
      </div>
      <ul class="divide-y divide-gray-100 dark:divide-gray-600">
        <li v-for="p in permissions" :key="p.module + '::' + p.name"
          :class="['px-4 py-4 flex items-start gap-4', p.applicable ? '' : 'opacity-60']">
          <div class="flex-1">
            <div class="font-medium dark:text-gray-100">{{ p.label }}</div>
            <div class="text-xs text-gray-500 mt-1">{{ p.description }}</div>
            <div class="flex flex-wrap gap-1.5 mt-2 text-[11px]">
              <span v-if="p.applies_to_names && p.applies_to_names.length"
                class="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100">
                Extendable to: {{ p.applies_to_names.join(', ') }}
              </span>
              <span v-if="p.native_names && p.native_names.length"
                class="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-100">
                Granted natively to: {{ p.native_names.join(', ') }}
              </span>
              <span v-if="p.granted_by_role"
                class="inline-block px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                Already granted by role
              </span>
              <span v-else-if="!p.applicable"
                class="inline-block px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-200">
                Not applicable to this user's role
              </span>
            </div>
            <div class="text-[10px] text-gray-400 mt-1 font-mono">{{ p.module }}.{{ p.name }}</div>
          </div>
          <label :class="['inline-flex items-center pt-1', p.applicable ? 'cursor-pointer' : 'cursor-not-allowed']"
            :title="p.applicable ? '' : `This permission only applies to: ${(p.applies_to_names || []).join(', ')}`">
            <input type="checkbox" :checked="p.granted" :disabled="!p.applicable"
              @change="setPermission(p, $event.target.checked)" class="sr-only peer">
            <div
              class="w-11 h-6 bg-gray-200 dark:bg-gray-500 rounded-full peer peer-checked:bg-yellow-500 peer-disabled:opacity-50 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5">
            </div>
          </label>
        </li>
        <li v-if="!permissions.length" class="text-center py-8 text-gray-500">Permission catalog is empty.</li>
      </ul>
    </div>

    </div>

    <!-- =========== ADDRESS BOOK TAB =========== -->
    <div v-if="activeTab === 'addressbook'"
      class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-sm overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
        <div class="font-medium dark:text-gray-100">Address book entries (audit)</div>
        <div class="text-xs text-gray-500 break-all">
          Frozen historical records linked by email <code
            class="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-xs">{{ summary.email }}</code>.
          Read-only — kept for audit per the 2026-06-05 decision.
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-sm min-w-[900px]">
          <thead class="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-700 dark:text-gray-300">
            <tr>
              <th class="text-left px-4 py-3">AB id</th>
              <th class="text-left px-4 py-3">Company</th>
              <th class="text-left px-4 py-3">Name</th>
              <th class="text-left px-4 py-3">Work phone</th>
              <th class="text-left px-4 py-3">Cell phone</th>
              <th class="text-left px-4 py-3">Type</th>
              <th class="text-left px-4 py-3">Status</th>
              <th class="text-left px-4 py-3">Last touched</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
            <tr v-for="row in addressBookRows" :key="row.id" class="dark:text-gray-100">
              <td class="px-4 py-3 text-gray-500">{{ row.id }}</td>
              <td class="px-4 py-3">{{ row.company_name || '—' }}</td>
              <td class="px-4 py-3">{{ row.name_first }} {{ row.name_last }}</td>
              <td class="px-4 py-3">{{ row.phone_primary }}<span v-if="row.phone_primary_ext"> x{{ row.phone_primary_ext
                  }}</span></td>
              <td class="px-4 py-3">{{ row.phone_secondary }}<span v-if="row.phone_secondary_ext"> x{{
                row.phone_secondary_ext }}</span></td>
              <td class="px-4 py-3">{{ row.user_type }}</td>
              <td class="px-4 py-3">
                <span
                  :class="row.retired ? 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200' : 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'"
                  class="text-xs px-2 py-0.5 rounded-full">
                  {{ row.retired ? 'Retired' : 'Active' }}
                </span>
              </td>
              <td class="px-4 py-3 text-gray-500">{{ formatDate(row.tmstmp) }}</td>
            </tr>
            <tr v-if="!addressBookRows.length">
              <td colspan="8" class="text-center py-8 text-gray-500">No address book entries.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <UserEditHistoryModal :is-visible="historyOpen" :user-id="userId" @close="historyOpen = false" />
  </div>
</template>

<script>
import axios from 'axios';
import UserEditHistoryModal from './modals/UserEditHistoryModal.vue';

export default {
  components: { UserEditHistoryModal },
  data() {
    return {
      userId: null,
      activeTab: 'profile',
      summary: {},
      form: {},
      partTime: false,
      notifyCreated: false,
      notifyClosed: false,
      notifyBusy: false,
      profileActive: true,
      userTypePermanent: false,
      saving: false,
      savingUserType: false,
      dangerBusy: false,
      historyOpen: false,
      // admin set-password
      newPassword: '',
      confirmPassword: '',
      settingPassword: false,
      pwError: '',
      // roles
      roles: [],
      roleBusy: false,
      currentUserRoles: [],
      // manager + department (internal users only)
      managers: [],
      departments: [],
      // office locations (external users only)
      officeLocations: [],
      // companies
      companies: [],
      companySearch: '',
      companyResults: [],
      companySearchTimer: null,
      // permissions (curated FE catalog)
      permissions: [],
      // address book audit
      addressBookRows: [],
    };
  },
  computed: {
    isContactUser() {
      // Contacts (role 100) are external requesters who never log into the portal,
      // so permission overrides don't apply to them — the Permissions tab is hidden.
      return Array.isArray(this.summary.role_ids) && this.summary.role_ids.includes(100);
    },
    isAzureSourced() {
      // Mirror the Assets.vue Source column: any non-empty azure_id means the user
      // was provisioned via Azure / Entra ID; otherwise treat the record as manual.
      const id = this.summary.azure_id;
      return !!(id && String(id).trim() !== '');
    },
    isInternalUser() {
      // Internal = holds no external role (60/70/80/100). Manager + department fields
      // are AIM-staff attributes only shown for internal users.
      const ids = Array.isArray(this.summary.role_ids) ? this.summary.role_ids : [];
      return !ids.some(r => [60, 70, 80, 100].includes(Number(r)));
    },
    isExternalUser() {
      // External = holds an external role. Initials + office location are shown for these.
      return !this.isInternalUser;
    },
    callerMinRole() {
      // The current admin's highest privilege = smallest role id they hold. They can
      // manage roles at or below their own privilege level (id >= this).
      const ids = (this.currentUserRoles || []).map(Number).filter(n => !isNaN(n));
      return ids.length ? Math.min(...ids) : 9999;
    },
    targetMinRole() {
      // The edited user's highest privilege = smallest active role id they hold.
      const ids = Array.isArray(this.summary.role_ids) ? this.summary.role_ids.map(Number) : [];
      return ids.length ? Math.min(...ids) : 9999;
    },
    canSetPassword() {
      // Allowed when the admin is at or above the target's privilege level
      // (caller's smallest role id <= target's smallest role id).
      return this.callerMinRole <= this.targetMinRole;
    },
    canManageStatus() {
      // Same rule as password/role management: only a target at or below your level.
      return this.callerMinRole <= this.targetMinRole;
    },
    managerOptions() {
      // Source list is roles 10/20 (from the API). If the user already has a manager
      // who isn't in that list, surface it anyway so saving doesn't silently clear it.
      const list = [...this.managers];
      const cur = Number(this.form.manager_id || 0);
      if (cur && !list.some(m => Number(m.id) === cur)) {
        list.unshift({ id: cur, name: `Current manager (#${cur})` });
      }
      return list;
    },
    tabs() {
      const list = [
        { key: 'profile', label: 'Profile' },
        { key: 'roles', label: 'Roles', badge: this.summary.roles_count ?? 0 },
        { key: 'companies', label: 'Companies', badge: this.summary.companies_count ?? 0 },
      ];
      // Shown for everyone: external users get the ticket-notification toggles here,
      // internal / non-contact users also get the frontend page-override list.
      list.push({ key: 'permissions', label: 'Permissions' });
      list.push({ key: 'addressbook', label: 'Address book', badge: this.summary.address_book_count ?? 0 });
      return list;
    },
    displayName() {
      const full = (this.summary.name || '').trim();
      if (full) return full;
      const email = (this.summary.email || '').trim();
      if (!email) return 'User #' + this.userId;
      return email.split('@')[0] || email;
    },
    initials() {
      const f = (this.summary.name_first || '').charAt(0).toUpperCase();
      const l = (this.summary.name_last || '').charAt(0).toUpperCase();
      if (f || l) return (f + l) || '?';
      // No name → derive initials from the email local part / displayName
      const fallback = this.displayName;
      return (fallback.charAt(0) || '?').toUpperCase();
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(v) {
        this.userId = Number(v);
        this.loadAll();
      },
    },
    activeTab(tab) {
      if (tab === 'roles' && !this.roles.length) this.loadRoles();
      if (tab === 'companies' && !this.companies.length) this.loadCompanies();
      // The page-override list is hidden for contacts, so only fetch it for non-contacts.
      if (tab === 'permissions' && !this.isContactUser && !this.permissions.length) this.loadPermissions();
      if (tab === 'addressbook' && !this.addressBookRows.length) this.loadAddressBook();
    },
  },
  methods: {
    async loadAll() {
      this.$updateLoading(1);
      try {
        await this.loadSummary();
        await this.load();
        this.loadManagersAndDepartments();
        this.loadOfficeLocations();
        this.loadCurrentUserRoles();
      } finally {
        this.$updateLoading(-1);
      }
    },
    async loadCurrentUserRoles() {
      try {
        const res = await axios.get(`user/role/${localStorage.getItem('user_id')}`);
        this.currentUserRoles = Array.isArray(res.data?.roles) ? res.data.roles : [];
      } catch (e) {
        this.currentUserRoles = [];
      }
    },
    async loadOfficeLocations() {
      try {
        const res = await axios.get(`users/${this.userId}/office-locations`);
        this.officeLocations = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.officeLocations = [];
      }
    },
    async loadManagersAndDepartments() {
      // Only needed for the internal-user Manager / Department dropdowns.
      try {
        const [mgrs, depts] = await Promise.all([
          axios.get('users/managers'),
          axios.get('departments/'),
        ]);
        this.managers = Array.isArray(mgrs.data) ? mgrs.data : [];
        this.departments = Array.isArray(depts.data) ? depts.data : [];
      } catch (e) {
        this.managers = this.managers || [];
        this.departments = this.departments || [];
      }
    },
    async loadSummary() {
      try {
        const res = await axios.get(`users/${this.userId}/summary`);
        this.summary = res.data || {};
      } catch (e) { /* handled in load() if user not found */ }
    },
    async load() {
      try {
        const res = await axios.post('requesters/getuser', { id: this.userId });
        const u = (res.data && typeof res.data === 'object') ? res.data : null;
        if (!u || u === 0) {
          this.form = {};
          return;
        }
        this.form = { ...u };
        this.partTime = !!u.parttime;
        this.notifyCreated = !!u.email_urgent;
        this.notifyClosed = !!u.email_closed;
        this.userTypePermanent = !!u.user_type_override_permanent;
        this.profileActive = !u.retired;
      } catch (e) {
        console.error('Failed to load user', e);
      }
    },
    async save() {
      this.saving = true;
      this.$updateLoading(1);
      try {
        const payload = {
          name_first: this.form.name_first,
          name_last: this.form.name_last,
          email: this.form.email,
          phone_primary: this.form.phone_primary,
          phone_primary_ext: this.form.phone_primary_ext,
          phone_secondary: this.form.phone_secondary,
          phone_secondary_ext: this.form.phone_secondary_ext,
          notes: this.form.notes,
          retired: this.profileActive ? 0 : 1,
        };
        // Job title, manager, and department are managed differently per user type:
        //  - Internal: title is editable; manager + aim_dept are editable here.
        //  - External: title + department come from the Azure sync (read-only) — don't
        //    send them, so a resave never clobbers the Azure-managed values.
        // The backend additionally enforces internal-admin + internal-target for
        // manager_id / aim_dept writes.
        if (this.isInternalUser) {
          payload.title = this.form.title;
          payload.manager_id = Number(this.form.manager_id || 0);
          payload.aim_dept = Number(this.form.aim_dept || 0);
          payload.parttime = this.partTime ? 1 : 0;
          payload.emergency_name = this.form.emergency_name || '';
          payload.emergency_relation = this.form.emergency_relation || '';
          payload.emergency_phone = this.form.emergency_phone || '';
        } else {
          // External users: initials + office location (backend gates on external target).
          payload.initials = this.form.initials || '';
          payload.officelocation = Number(this.form.officelocation || 0);
        }
        const res = await axios.post(`requesters/edit/${this.userId}`, payload);
        if (res.data && res.data.address_book) {
          this.form = { ...res.data.address_book };
        }
        await this.loadSummary();
      } catch (e) {
        alert(e?.response?.data?.message || 'Save failed.');
      } finally {
        this.saving = false;
        this.$updateLoading(-1);
      }
    },
    async saveUserType() {
      if (!this.form.user_type) return;
      this.savingUserType = true;
      this.$updateLoading(1);
      try {
        const res = await axios.post(`requesters/user-type/${this.userId}`, {
          user_type: this.form.user_type,
          permanent: this.userTypePermanent,
        });
        if (res.data && res.data.data) {
          this.form.user_type = res.data.data.user_type;
          this.form.user_type_overridden = res.data.data.user_type_overridden;
          this.form.user_type_override_permanent = res.data.data.user_type_override_permanent;
        }
      } catch (e) {
        alert(e?.response?.data?.message || 'User type override failed.');
      } finally {
        this.savingUserType = false;
        this.$updateLoading(-1);
      }
    },
    // ---- Company ticket notifications (Permissions tab) ----
    async saveNotifyFlags() {
      // Persist the two opt-ins to atms.users.flags immediately. requesters/edit merges
      // these into the flags JSON (email_urgent = created, email_closed = closed) and the
      // send logic (EmailService.getClientAdmin) reads them per company user.
      if (this.notifyBusy) return;
      this.notifyBusy = true;
      try {
        await axios.post(`requesters/edit/${this.userId}`, {
          email_urgent: this.notifyCreated ? 1 : 0,
          email_closed: this.notifyClosed ? 1 : 0,
        });
        this.form.email_urgent = this.notifyCreated ? 1 : 0;
        this.form.email_closed = this.notifyClosed ? 1 : 0;
      } catch (e) {
        alert(e?.response?.data?.message || 'Failed to save notification setting.');
        // Resync the toggles to the last-known server truth so the UI never lies.
        this.notifyCreated = !!this.form.email_urgent;
        this.notifyClosed = !!this.form.email_closed;
      } finally {
        this.notifyBusy = false;
      }
    },
    // ---- Roles tab ----
    async loadRoles() {
      this.$updateLoading(1);
      try {
        const res = await axios.get(`users/${this.userId}/roles`);
        this.roles = Array.isArray(res.data) ? res.data : [];
      } finally {
        this.$updateLoading(-1);
      }
    },
    async toggleRole(role, on) {
      if (role.id < this.callerMinRole) {
        // Privilege guard — can only manage roles at or below your own privilege level.
        this.loadRoles();
        return;
      }
      if (this.roleBusy) {
        // A request is already in flight — undo this stray toggle by resyncing.
        this.loadRoles();
        return;
      }
      // Hard guard: external roles (60/70/80) can't function without a company link.
      // Block up front with an actionable message instead of writing a dormant role.
      if (on && role.requires_company && !(this.summary.companies_count > 0)) {
        alert('External roles require at least one company. Add a company on the Companies tab first.');
        this.loadRoles();   // revert the checkbox to its server state
        return;
      }
      this.roleBusy = true;
      this.$updateLoading(1);
      try {
        const res = await axios.post(`users/${this.userId}/roles`, { role_id: role.id, active: !!on });
        if (res.data && res.data.warning) alert(res.data.message);
        role.active = !!on;
        await this.loadSummary();
        // Permission applicability is derived from the user's roles — drop the cached
        // permissions list so the next visit to the Permissions tab refetches with the
        // updated applicable/granted_by_role flags.
        this.permissions = [];
      } catch (e) {
        alert(e?.response?.data?.message || 'Failed to update role.');
        // Resync the toggle to the server's truth so the UI never lies about state.
        await this.loadRoles();
      } finally {
        this.roleBusy = false;
        this.$updateLoading(-1);
      }
    },
    // ---- Companies tab ----
    async loadCompanies() {
      this.$updateLoading(1);
      try {
        const res = await axios.get(`users/${this.userId}/companies`);
        this.companies = Array.isArray(res.data) ? res.data : [];
      } finally {
        this.$updateLoading(-1);
      }
    },
    debouncedCompanySearch() {
      clearTimeout(this.companySearchTimer);
      this.companySearchTimer = setTimeout(async () => {
        const res = await axios.get('companies/search', { params: { q: this.companySearch } });
        this.companyResults = Array.isArray(res.data) ? res.data : [];
      }, 250);
    },
    async addCompany(c) {
      this.$updateLoading(1);
      try {
        await axios.post(`users/${this.userId}/companies/add`, { company_id: c.id });
        this.companyResults = [];
        this.companySearch = '';
        await this.loadCompanies();
        await this.loadSummary();
      } finally {
        this.$updateLoading(-1);
      }
    },
    async removeCompany(c) {
      if (!confirm(`Remove ${c.companyname}?`)) return;
      this.$updateLoading(1);
      try {
        await axios.delete(`users/${this.userId}/companies/${c.company_id}`);
        await this.loadCompanies();
        await this.loadSummary();
      } finally {
        this.$updateLoading(-1);
      }
    },
    // ---- Permissions tab ----
    async loadPermissions() {
      this.$updateLoading(1);
      try {
        const res = await axios.get(`users/${this.userId}/permissions`);
        this.permissions = Array.isArray(res.data) ? res.data : [];
      } finally {
        this.$updateLoading(-1);
      }
    },
    async setPermission(p, granted) {
      // Optimistic update so the toggle feels instant
      const prev = p.granted;
      p.granted = !!granted;
      try {
        await axios.post(`users/${this.userId}/permissions`, {
          module: p.module, name: p.name, granted: !!granted,
        });
      } catch (e) {
        p.granted = prev;
        alert(e?.response?.data?.message || 'Failed to save permission.');
      }
    },
    // ---- Address book tab ----
    async loadAddressBook() {
      this.$updateLoading(1);
      try {
        const res = await axios.get(`users/${this.userId}/address-book`);
        this.addressBookRows = Array.isArray(res.data) ? res.data : [];
      } finally {
        this.$updateLoading(-1);
      }
    },
    openHistory() {
      this.historyOpen = true;
    },
    // ---- Danger zone ----
    async setUserPassword() {
      this.pwError = '';
      if ((this.newPassword || '').length < 8) {
        this.pwError = 'Password must be at least 8 characters.';
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.pwError = 'Passwords do not match.';
        return;
      }
      if (!confirm(`Set a new password for ${this.summary.email}? This takes effect immediately.`)) return;
      this.settingPassword = true;
      this.$updateLoading(1);
      try {
        await axios.post(`users/${this.userId}/set-password`, {
          password: this.newPassword,
          password_confirmation: this.confirmPassword,
        });
        this.newPassword = '';
        this.confirmPassword = '';
        alert('Password updated.');
      } catch (e) {
        this.pwError = e?.response?.data?.message || 'Failed to set password.';
      } finally {
        this.settingPassword = false;
        this.$updateLoading(-1);
      }
    },
    async sendPasswordReset() {
      if (!confirm(`Send a password-reset email to ${this.summary.email}?`)) return;
      this.dangerBusy = true;
      this.$updateLoading(1);
      try {
        const res = await axios.post(`users/${this.userId}/password-reset`);
        alert(res?.data?.message || 'Reset email sent.');
      } catch (e) {
        alert(e?.response?.data?.message || 'Failed to send reset email.');
      } finally {
        this.dangerBusy = false;
        this.$updateLoading(-1);
      }
    },
    async revokeTokens() {
      if (!confirm("Revoke all of this user's access tokens? They'll be logged out everywhere.")) return;
      this.dangerBusy = true;
      this.$updateLoading(1);
      try {
        const res = await axios.post(`users/${this.userId}/revoke-tokens`);
        alert(res?.data?.message || 'Tokens revoked.');
      } catch (e) {
        alert(e?.response?.data?.message || 'Failed to revoke tokens.');
      } finally {
        this.dangerBusy = false;
        this.$updateLoading(-1);
      }
    },
    async disableAccount() {
      if (!confirm('Disable this account? They will no longer be able to sign in and existing tokens are revoked.')) return;
      this.dangerBusy = true;
      this.$updateLoading(1);
      try {
        const res = await axios.post(`users/${this.userId}/disable`);
        alert(res?.data?.message || 'Account disabled.');
        await this.loadSummary();
        await this.load();
      } catch (e) {
        alert(e?.response?.data?.message || 'Failed to disable account.');
      } finally {
        this.dangerBusy = false;
        this.$updateLoading(-1);
      }
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleDateString();
    },
    formatDateTime(value) {
      if (!value) return 'Never';
      // Stored in UTC. A bare MySQL datetime ("YYYY-MM-DD HH:MM:SS") carries no zone
      // and would be parsed as local time — normalize to UTC, then render in the
      // browser's local timezone. ISO strings that already carry a zone pass through.
      let s = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(s)) {
        s = s.replace(' ', 'T') + 'Z';
      }
      const d = new Date(s);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
  },
};
</script>
